import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';

import HeaderV2 from '../../components/headerV2/HeaderV2';
import FooterV2 from '../../components/footerV2/FooterV2';
import AboutHero from '../../components/aboutHero/AboutHero';
import useScrollReveal from '../../hooks/useScrollReveal';

import metadataJSON from "../../data/metadata.json";
import schemadataJSON from "../../data/schemadata.json";
import './aboutV2.css';

/* Team photo supplied in the Sep 2026 website review (lives in /public). */
const WhoWeAreImg = process.env.PUBLIC_URL + "/images/about-v2/who-we-are.jpg";

const pillars = [
    {
        icon: 'fa-bullseye',
        title: 'Our Mission',
        text: "To empower individuals, inspire community action, and create lasting social impact through Life Saver Army, ensuring timely access to safe blood, promoting voluntary blood donation, and strengthening emergency healthcare support across India.",
    },
    {
        icon: 'fa-eye',
        title: 'Our Vision',
        text: "To build a future where every community has the awareness, participation, and infrastructure needed to ensure timely access to voluntary blood donation, so that no life is lost during a medical emergency.",
    },
    {
        icon: 'fa-heart',
        title: 'Our Values',
        text: "We are driven by urgency, compassion, integrity, and community, guiding every decision, partnership, and initiative we undertake while building trust, inspiring action, and creating meaningful, lasting impact across society.",
    },
];

const WhoWeAre = ({ path }) => {

    // Setting metadata and schemadata
    const [metadata, setMetadata] = useState({ title: "JJFIndia | About us" });
    const [schemadata, setSchemadata] = useState({});

    const rootRef = useRef(null);
    useScrollReveal(rootRef);

    // Getting the schemadata and setting the schema data.
    useEffect(() => {
        let tempMetadata = metadataJSON[path];
        let tempSchemadata = schemadataJSON[path];

        if (tempMetadata) {
            setMetadata(tempMetadata);
        } else {
            tempMetadata = {
                title: "JJFIndia | About us",
                description: "JJFIndia About us page",
                keywords: "",
                robots: "index, follow",
                canonical: "https://jjfindia.com/about-us/who-we-are",
                openGraph: {
                    title: "JJFIndia | About us",
                    description: "JJFIndia About us page",
                    url: "https://jjfindia.com/about-us/who-we-are"
                },
                twitter: {
                    card: "summary_large_image",
                    title: "JJFIndia | About us",
                    description: "JJFIndia About us page"
                }
            }

            setMetadata(tempMetadata);
        }

        if (tempSchemadata) {
            setSchemadata(tempSchemadata);
        }

    }, [path]);

    return (
        <Fragment>
            <Helmet>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
                    rel="stylesheet"
                />
                <title>{metadata.title}</title>
                <meta name="description" content={metadata.description} />
                <meta name="keywords" content={metadata.keywords} />
                <meta name="robots" content={metadata.robots} />
                <link rel="canonical" href={metadata.canonical} />
                <meta property="og:title" content={metadata.openGraph?.title} />
                <meta property="og:description" content={metadata.openGraph?.description} />
                <meta property="og:url" content={metadata.canonical} />
                <meta name="twitter:title" content={metadata.twitter?.title} />
                <meta name="twitter:description" content={metadata.twitter?.description} />
                <script type="application/ld+json">{JSON.stringify(schemadata, null, 2)}</script>
            </Helmet>

            <div className="jjfv2 about-live" ref={rootRef}>
                <HeaderV2 />

                <main className="page_content">
                    {/* ---------- Page hero ---------- */}
                    <AboutHero
                        eyebrow="Get to know us"
                        title="Who We Are?"
                        crumbs={[{ label: 'Home', to: '/' }, { label: 'About Us' }]}
                    />

                    {/* ---------- Who we are: intro ---------- */}
                    <section className="section section--mist about" id="who-we-are">
                        <div className="wrap split">
                            <div className="about-media reveal">
                                <span className="leaf-frame" aria-hidden="true"></span>
                                <div className="ph">
                                    <img src={WhoWeAreImg} alt="Jainam Jivika Foundation team with Life Saver Army volunteers" loading="lazy" />
                                </div>
                            </div>
                            <div className="about-copy reveal d1">
                                <span className="eyebrow">Who we are</span>
                                <h2>A Foundation Built on Service</h2>
                                <p className="lead">
                                    Jainam Jivika Foundation is a social organisation working to bring lasting,
                                    positive change to communities across India. We take up the causes that
                                    matter most and act where the need is greatest - beginning with our most
                                    urgent priority through Life Saver Army, our initiative making sure no
                                    patient loses their life for want of blood.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* ---------- Mission / Vision / Values ---------- */}
                    <section className="section">
                        <div className="wrap">
                            <div className="about-head">
                                <span className="eyebrow">What drives us</span>
                                <h2>Our Mission, Vision &amp; Values</h2>
                                <p className="lead">
                                    A social foundation built around one urgent purpose; ensuring no patient in
                                    India loses their life for want of blood.
                                </p>
                            </div>
                            <div className="pillars">
                                {pillars.map((pillar, index) => (
                                    <article className={"pillar reveal" + (index ? " d" + index : "")} key={index}>
                                        <div className="pic">
                                            <i className={`fa-solid ${pillar.icon}`}></i>
                                        </div>
                                        <h3>{pillar.title}</h3>
                                        <p>{pillar.text}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* ---------- About the foundation ---------- */}
                    <section className="section section--mist" id="feature">
                        <div className="wrap">
                            <div className="about-head">
                                <span className="eyebrow">About foundation</span>
                                <h2>Jainam Jivika Foundation; More Than Just a Foundation</h2>
                            </div>
                            <div className="about-prose about-prose--split reveal">
                                <div>
                                    <p>
                                        Jainam Jivika Foundation is a social organisation dedicated to improving
                                        lives and bringing meaningful change to communities across India. Our goal
                                        is to create a lasting impact by focusing on the areas that matter most;
                                        beginning with the most urgent.
                                    </p>
                                    <p>
                                        At Jainam Jivika Foundation, we believe in working together to solve
                                        today's challenges and build stronger, kinder, more resilient communities.
                                    </p>
                                    <p>
                                        At Jainam Jivika Foundation, we are not just responding to problems; we are
                                        building solutions and creating possibilities for a better world.
                                    </p>
                                </div>

                                <div className="about-callout">
                                    <span className="about-callout__badge">
                                        <i className="fa-solid fa-droplet"></i>
                                        Life Saver Army
                                    </span>
                                    <p>
                                        Our flagship initiative, Life Saver Army, is a nationwide emergency
                                        blood donor network. Anyone can register as a donor in minutes by scanning a
                                        Life Saver Army QR code; our field coordinators and hospital partners
                                        then connect registered donors with patients nearby, so no one is left
                                        searching for blood in an emergency. From Varanasi to Goa, from Bihar to Maharashtra,
                                        our teams work every day to build the network that saves lives.
                                    </p>
                                    <p>
                                        At Life Saver Army, every registered donor is a potential lifesaver.
                                        Every QR code sticker placed in a hospital corridor, railway station, or
                                        college campus is an invitation for someone to join a network that
                                        exists for one purpose: ensuring no patient loses their life for want of
                                        blood.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>

                <FooterV2 />
            </div>
        </Fragment>
    )
};

export default WhoWeAre;
